import { useEffect, useState } from 'react'
import { Box, Button, Collapse, Paper, Typography } from '@mui/material'

import { Profile } from './Profile'
import BouncingArrowIcon from './BouncingArrowIcon'
import { SocialLinkList } from '../../common/domain/sharable/videopage/SocialLinkList'
import JoinVibioCard from './JoinVibioCard'


export default function ProfileCard({ userData, socials, linkButtons, theme, onLinkClick }) {
    const [isProfileExpanded, setProfileExpanded] = useState(true)
    const toggleProfile = () => { setProfileExpanded(!isProfileExpanded) }
    const [textColor, setTextColor] = useState("#f6f6f6")
    const [bioTextColor, setBioTextColor] = useState("#000")

    useEffect(() => {
        if (theme) {
            const updateTextColors = () => {
                if (isLightColor(theme?.buttonColor)) {
                    setTextColor("#040404")
                } else {
                    setTextColor("#f6f6f6")
                }

                if (isLightColor(theme?.bgColor)) {
                    setBioTextColor("#0c0c0c")
                } else {
                    setBioTextColor("#fff")
                }
            }

            updateTextColors()
        }
    }, [theme])


    if (!userData || theme === null) {
        return <Typography>Loading user data...</Typography>
    }

    const isLightColor = (color) => {
        const r = parseInt(color.substring(0, 2), 16) / 255
        const g = parseInt(color.substring(2, 4), 16) / 255
        const b = parseInt(color.substring(4, 6), 16) / 255

        const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
        return luminance > 0.7
    }

    const fontStyles = {
        REGULAR: 'Catamaran',
        ELEGANT: 'Times New Roman',
        BOLD: 'Bevan',
        MONO: 'monospace',
        PLAYFUL: 'Comic Neue',
    }


    const buttonStyles = {
        FILLED_REGULAR: { backgroundColor: `#${theme.buttonColor}`, borderRadius: 0 },
        FILLED_ROUND: { backgroundColor: `#${theme.buttonColor}`, borderRadius: '32px' },
        OUTLINE_REGULAR: {
            backgroundColor: 'transparent',
            border: `3px solid #${theme.buttonColor}`,
            color: "#000",
            borderRadius: 0
        },
        OUTLINE_ROUND: {
            backgroundColor: 'transparent',
            border: `3px solid #${theme.buttonColor}`,
            borderRadius: '32px',
            color: "#000",
        },
        SHADOW_REGULAR: {
            backgroundColor: `#${theme.buttonColor}`,
            boxShadow: '3px 3px 2px 3px #000',
            borderRadius: 0
        },
        SHADOW_ROUND: {
            backgroundColor: `#${theme.buttonColor}`,
            boxShadow: '3px 3px 2px 3px #000',
            borderRadius: '32px',
        },
    }

    return (
        <Button onClick={toggleProfile}
            sx={{
                width: '100%',
                padding: 0,
                textTransform: 'none',
                color: 'text.second'
            }}>
            <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: '.2rem',
                right: '.2rem',
                paddingBottom: '1rem',
                backgroundColor: 'transparent',
                borderRadius: '20px',
                maxHeight: '88vh',
            }}>
                {/* profile */}
                <Profile
                    isFullView={isProfileExpanded}
                    userData={userData}
                    color={theme?.buttonColor}
                    bioTxtColor={bioTextColor}
                    fontColor={textColor}
                />

                {/* social links */}
                <SocialLinkList
                    socials={socials}
                    iconColor={theme?.iconColor}
                    onLinkClick={onLinkClick}
                />

                {!isProfileExpanded && <BouncingArrowIcon />}


                {/* link buttons */}
                <Collapse in={isProfileExpanded}>
                    <div style={{
                        overflowY: 'auto',
                        maxHeight: '50vh',
                    }}>
                        {
                            linkButtons
                                .sort((a, b) => a.position - b.position)
                                .map((btn) => (
                                    <Button
                                        key={btn.id}
                                        href={btn.url.startsWith("http") ? btn.url : `https://${btn.url}`}
                                        onClick={() => onLinkClick?.('LINK_BUTTON', btn.id)}
                                        sx={{
                                            display: 'block',
                                            color: textColor,
                                            ":hover": {
                                                backgroundColor: '#FF1493',
                                            },
                                            m: '1rem 1.5rem',
                                            p: '.6rem',
                                            maxWidth: '100%',
                                            boxSizing: 'border-box',
                                            ...buttonStyles[theme.buttonStyle]
                                        }}>
                                        <Typography
                                            variant="body1"
                                            component="p"
                                            sx={{
                                                fontWeight: 500,
                                                fontFamily: `${fontStyles[theme.fontStyle]}`,
                                                textAlign: 'center',
                                                textTransform: 'none',
                                                // Keep long titles from overflowing: break long tokens, clamp to 2 lines.
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                overflowWrap: 'anywhere',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {btn.title}
                                        </Typography>
                                    </Button>
                                ))
                        }
                    </div>
                    {/* Vibio signature */}
                    {userData?.tier === 'LIGHT' && <JoinVibioCard username={userData.username} />}
                </Collapse>
            </Box>
        </Button>
    )
}
